import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider
} from '@mui/material';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CropIcon from '@mui/icons-material/Crop';
import Avatar from '@mui/material/Avatar';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';

const GOLD = '#d4af37';

const MAX_AVATAR_SIZE = 200;

/** Canvas helper — crops and resizes to max 200x200 for avatar use, returns a compressed base64 string */
function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const size = Math.min(pixelCrop.width, pixelCrop.height, MAX_AVATAR_SIZE);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('No 2d context')); return; }
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, size, size
      );
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    image.onerror = () => reject(new Error('Image load failed'));
    image.src = imageSrc;
  });
}

interface CommitteeMember {
  role: string;
  name: string;
  phone?: string;
  photoUrl?: string;
}

interface AdministrationContent {
  title: string;
  description: string;
  managementStructure: string;
  templesAuthority: string;
  administrativeBodies: string;
  committeeMembers: CommitteeMember[];
  responsibilitiesDescription: string;
  visionStatement: string;
  missionStatement: string;
  personImages: Record<string, string>;
}

// All officials from the public Administration page — grouped by section
const allOfficials = [
  { section: 'Kottayil Temple', persons: [
    { name: 'Brahmasri Meppallimana Unnikrishnan Adithirippadu', role: 'Temple Thantri' },
    { name: 'Brahmasri Marakkattillath Unnikrishnan Namboothiri', role: 'Melsanthi' },
  ]},
  { section: 'Kovilakam Temple', persons: [
    { name: 'Brahmasri Mundottu Puliyapadambu Kuberan Somayajippadu', role: 'Kshethram Thantri' },
    { name: 'Brahmasri Edamana Unnikrishnan Namboodiri', role: 'Melsanthi' },
  ]},
  { section: 'Temple Manager', persons: [
    { name: 'PK Somasekharan Vayangottu', role: 'Manager' },
  ]},
  { section: 'Hereditary Trustees', persons: [
    { name: 'Mangoottil Asokan', role: 'Chairman' },
    { name: 'Mangoottil Unni Nair Puthussery', role: 'Hereditary Trustee' },
    { name: 'Mangoottil Venu Nair, Punathil', role: 'Hereditary Trustee' },
    { name: 'Mangoottil Sasi, Elavana', role: 'Hereditary Trustee' },
    { name: 'Mangoottil Ramachandran Nair', role: 'Hereditary Trustee' },
    { name: 'Madhu Mangoottil', role: 'Hereditary Trustee' },
    { name: 'Sanjay Marakkattu', role: 'Hereditary Trustee' },
  ]},
  { section: 'Non-Hereditary Trustees', persons: [
    { name: 'Prakasan Nellimadathil', role: 'Vice Chairman' },
    { name: 'Radhakrishnan T.C. Thazhe Cherukunnummal', role: 'Non-Hereditary Trustee' },
    { name: 'Raghunath Othayoth', role: 'Non-Hereditary Trustee' },
    { name: 'Damodaran Nair Pottakkattu', role: 'Non-Hereditary Trustee' },
    { name: 'Bijesh Ramanilayam', role: 'Non-Hereditary Trustee' },
    { name: 'V.K Manojkumar', role: 'Non-Hereditary Trustee' },
    { name: 'Rajeesh Edavalath', role: 'Non-Hereditary Trustee' },
  ]},
  { section: 'Temple Welfare Committee', persons: [
    { name: 'Srinivasan Nair Kizhakkedath', role: 'President' },
    { name: 'Karunan Kandiyil', role: 'Vice President' },
    { name: 'Rajan KP Aswaradham', role: 'Secretary' },
    { name: 'Ranjith MammiliMeethal', role: 'Joint Secretary' },
    { name: 'Babu Puthan Purayil', role: 'Treasurer' },
    { name: 'Gopalakrishnan Meethale Palayadi', role: 'Member' },
    { name: 'Asokan Padinjarayil', role: 'Member' },
    { name: 'Arjun Madathil', role: 'Member' },
    { name: 'Narayanan Master Kizhakke Raroth', role: 'Member' },
    { name: 'Madhavan Nair Padikkalakkandi', role: 'Member' },
    { name: 'Sunilkumar Vipanchika', role: 'Member' },
    { name: 'Babu Thadathil', role: 'Member' },
    { name: 'Sujith ValiyaValappil', role: 'Member' },
    { name: 'K.V Sankaran', role: 'Member' },
  ]},
];

const defaultContent: AdministrationContent = {
  title: 'Administration',
  description: 'Learn about our temple\'s governance and management',
  managementStructure: 'Organizational structure and leadership',
  templesAuthority: 'Temple authority and decision-making body',
  administrativeBodies: 'Various committees and their functions',
  committeeMembers: [
    { role: 'President', name: '' },
    { role: 'Secretary', name: '' },
    { role: 'Treasurer', name: '' },
  ],
  responsibilitiesDescription: 'Key responsibilities and operations',
  visionStatement: 'Our vision for the temple and community',
  missionStatement: 'Our mission to serve devotees and preserve traditions',
  personImages: {},
};

const AdministrationAboutManager = () => {
  const [content, setContent] = useState<AdministrationContent>(defaultContent);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploadingPerson, setUploadingPerson] = useState<string | null>(null);

  // Crop dialog state
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [cropPersonName, setCropPersonName] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'about_content', 'administration'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<AdministrationContent>;
          setContent({
            ...defaultContent,
            ...data,
            committeeMembers:
              Array.isArray(data.committeeMembers) && data.committeeMembers.length > 0
                ? data.committeeMembers
                : defaultContent.committeeMembers,
            personImages: (data.personImages as Record<string, string>) || {},
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'about_content', 'administration'), content);
      showSnackbar('Administration content saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (field: keyof AdministrationContent, value: string) => {
    setContent({ ...content, [field]: value });
  };

  const handleMemberChange = (index: number, field: keyof CommitteeMember, value: string) => {
    setContent(prev => ({
      ...prev,
      committeeMembers: prev.committeeMembers.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
    }));
  };

  const addMember = () => {
    setContent(prev => ({
      ...prev,
      committeeMembers: [...prev.committeeMembers, { role: '', name: '', phone: '', photoUrl: '' }],
    }));
  };

  const removeMember = (index: number) => {
    setContent(prev => ({
      ...prev,
      committeeMembers: prev.committeeMembers.filter((_, i) => i !== index),
    }));
  };

  /** Opens the crop dialog after selecting a file */
  const handlePersonImageSelect = (personName: string, file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Image must be under 5MB', 'error');
      return;
    }
    if (!file.type.startsWith('image/')) {
      showSnackbar('Only image files are allowed', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result as string);
      setCropPersonName(personName);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropDialogOpen(true);
    };
    reader.onerror = () => showSnackbar('Failed to read image', 'error');
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  /** Applies crop and stores the cropped base64 image */
  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !cropPersonName) return;
    setUploadingPerson(cropPersonName);
    setCropDialogOpen(false);
    try {
      const croppedBase64 = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      setContent(prev => ({
        ...prev,
        personImages: { ...prev.personImages, [cropPersonName]: croppedBase64 },
      }));
      showSnackbar('Image cropped & added — click Save to persist', 'success');
    } catch {
      showSnackbar('Failed to crop image', 'error');
    } finally {
      setUploadingPerson(null);
      setCropImageSrc(null);
      setCropPersonName(null);
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setCropImageSrc(null);
    setCropPersonName(null);
  };

  const handleRemovePersonImage = (personName: string) => {
    setContent(prev => {
      const updated = { ...prev.personImages };
      delete updated[personName];
      return { ...prev, personImages: updated };
    });
    showSnackbar('Image removed — click Save to persist', 'success');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Administration Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple governance and administration content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏛️ Administration Content
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Page Title"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={content.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Management Structure"
              value={content.managementStructure}
              onChange={(e) => handleChange('managementStructure', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Temple's Authority"
              value={content.templesAuthority}
              onChange={(e) => handleChange('templesAuthority', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Administrative Bodies"
              value={content.administrativeBodies}
              onChange={(e) => handleChange('administrativeBodies', e.target.value)}
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#000' }}>
                Committee Members (President / Secretary / etc.)
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addMember}
                variant="outlined"
                sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
              >
                Add Member
              </Button>
            </Box>
            {content.committeeMembers.map((member, index) => (
              <Card key={`${member.role}-${index}`} sx={{ mb: 2, border: `2px solid ${GOLD}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Member #{index + 1}
                    </Typography>
                    <IconButton onClick={() => removeMember(index)} color="error" aria-label="Remove member">
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Role (e.g., President)"
                        value={member.role}
                        onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={member.name}
                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone (optional)"
                        value={member.phone || ''}
                        onChange={(e) => handleMemberChange(index, 'phone', e.target.value)}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Photo URL (optional)"
                        value={member.photoUrl || ''}
                        onChange={(e) => handleMemberChange(index, 'photoUrl', e.target.value)}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Grid>

          {/* Official Photos Upload Section */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, mt: 2, mb: 1, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fefef8' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
                📷 Official Photos
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                Upload photos for each official. Images must be under 1MB. Click Save after uploading.
              </Typography>
              {allOfficials.map((section) => (
                <Box key={section.section} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#333', mb: 1.5, borderBottom: `1px solid ${GOLD}40`, pb: 0.5 }}>
                    {section.section}
                  </Typography>
                  <Grid container spacing={2}>
                    {section.persons.map((person) => {
                      const imgSrc = content.personImages[person.name];
                      const initials = person.name.split(' ').map(w => w.charAt(0)).slice(0, 2).join('').toUpperCase();
                      const isUploading = uploadingPerson === person.name;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={person.name}>
                          <Card sx={{ border: `1px solid ${GOLD}30`, borderRadius: 2 }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, '&:last-child': { pb: 2 } }}>
                              <Avatar
                                src={imgSrc || undefined}
                                sx={{ width: 56, height: 56, bgcolor: imgSrc ? 'transparent' : GOLD, fontSize: '1rem', fontWeight: 700 }}
                              >
                                {!imgSrc && initials}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {person.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: '#888', mt: 0.3 }}>
                                  {person.role}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                                  <Button
                                    component="label"
                                    size="small"
                                    variant="outlined"
                                    startIcon={isUploading ? <CircularProgress size={14} /> : <PhotoCameraIcon sx={{ fontSize: 14 }} />}
                                    disabled={isUploading}
                                    sx={{ fontSize: '0.65rem', py: 0.3, px: 1, borderColor: GOLD, color: GOLD, textTransform: 'none', '&:hover': { bgcolor: `${GOLD}15` } }}
                                  >
                                    {imgSrc ? 'Change' : 'Upload'}
                                    <input
                                      type="file"
                                      hidden
                                      accept="image/*"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handlePersonImageSelect(person.name, file);
                                        e.target.value = '';
                                      }}
                                    />
                                  </Button>
                                  {imgSrc && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemovePersonImage(person.name)}
                                      sx={{ color: '#ef4444', p: 0.5 }}
                                      aria-label="Remove photo"
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Responsibilities Description"
              value={content.responsibilitiesDescription}
              onChange={(e) => handleChange('responsibilitiesDescription', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Vision Statement"
              value={content.visionStatement}
              onChange={(e) => handleChange('visionStatement', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Mission Statement"
              value={content.missionStatement}
              onChange={(e) => handleChange('missionStatement', e.target.value)}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Crop Dialog */}
      <Dialog
        open={cropDialogOpen}
        onClose={handleCropCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CropIcon sx={{ color: GOLD }} /> Crop Image
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {cropImageSrc && (
            <Box sx={{ position: 'relative', width: '100%', height: 350, bgcolor: '#111' }}>
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </Box>
          )}
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              onChange={(_, value) => setZoom(value as number)}
              sx={{ color: GOLD }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCropCancel} sx={{ color: '#666' }}>Cancel</Button>
          <Button
            onClick={handleCropConfirm}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700, '&:hover': { bgcolor: '#b8941f' } }}
          >
            Apply Crop
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdministrationAboutManager;
